/** TanStack Query hooks for storage folders and folder-scoped file operations */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, API_BASE } from '../lib/api-client'

/**
 * JSON-serialized form of StorageFolderTree from @agentwiki/shared.
 * Dates are ISO strings (not Date objects) since they arrive via JSON API.
 */
export interface StorageFolderNode {
  id: string
  tenantId: string
  parentId: string | null
  name: string
  slug: string
  position: number
  createdBy: string
  createdAt: string
  updatedAt: string
  fileCount: number
  children: StorageFolderNode[]
}

/**
 * JSON-serialized form of Upload from @agentwiki/shared.
 * Dates are ISO strings (not Date objects) since they arrive via JSON API.
 */
export interface StorageFile {
  id: string
  fileKey: string
  filename: string
  contentType: string
  sizeBytes: number
  folderId: string | null
  extractionStatus: string | null // pending | processing | completed | failed | unsupported
  extractionMethod: string | null // vision | text | unsupported | null
  summary: string | null
  description: string | null
  lastSummaryAt: number | null // epoch ms (matches API serialization)
  /** 'meme': never extracted, summarized, searched, or sent to agent context */
  collection: 'meme' | null
  createdAt: string
}

// ── Folder tree ───────────────────────────────────────────────────────────────

export function useStorageFolderTree() {
  return useQuery<{ folders: StorageFolderNode[] }>({
    queryKey: ['storage-folders'],
    queryFn: () => apiClient.get<{ folders: StorageFolderNode[] }>('/api/storage-folders'),
  })
}

export function useCreateStorageFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; parentId?: string | null }) =>
      apiClient.post<{ id: string; name: string }>('/api/storage-folders', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['storage-folders'] }),
  })
}

export function useUpdateStorageFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string
      name?: string
      parentId?: string | null
      position?: number
    }) => apiClient.put<{ id: string }>(`/api/storage-folders/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['storage-folders'] }),
  })
}

export function useDeleteStorageFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ ok: boolean }>(`/api/storage-folders/${id}`),
    onSuccess: () => {
      // Invalidate both folders and all storage file queries (files reassigned to root)
      qc.invalidateQueries({ queryKey: ['storage-folders'] })
      qc.invalidateQueries({ queryKey: ['storage-files'] })
    },
  })
}

// ── Folder-scoped file listing ────────────────────────────────────────────────

const FILES_PAGE_SIZE = 50

/** folderId=null -> root files, folderId=undefined -> all files. Infinite scroll pagination. */
export function useStorageFiles(folderId?: string | null, enabled = true) {
  const folderParam =
    folderId === null ? 'root' : folderId !== undefined ? folderId : undefined

  return useInfiniteQuery<{ files: StorageFile[]; hasMore: boolean }>({
    queryKey: ['storage-files', folderId],
    enabled,
    queryFn: ({ pageParam = 0 }) => {
      // Storage browses every file, memes included (the API lists memes only when asked)
      const params = new URLSearchParams({ collection: 'all' })
      if (folderParam !== undefined) params.set('folderId', folderParam)
      params.set('limit', String(FILES_PAGE_SIZE))
      params.set('offset', String(pageParam))
      return apiClient.get<{ files: StorageFile[]; hasMore: boolean }>(`/api/uploads?${params}`)
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.reduce((sum, p) => sum + p.files.length, 0) : undefined,
  })
}

/** The meme collection across all folders, searched on the server by filename and description */
export function useMemeFiles(search: string, enabled = true) {
  const q = search.trim()
  return useInfiniteQuery<{ files: StorageFile[]; hasMore: boolean }>({
    queryKey: ['storage-files', 'memes', q],
    enabled,
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams({ collection: 'meme', limit: String(FILES_PAGE_SIZE), offset: String(pageParam) })
      if (q) params.set('q', q)
      return apiClient.get<{ files: StorageFile[]; hasMore: boolean }>(`/api/uploads?${params}`)
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.reduce((sum, p) => sum + p.files.length, 0) : undefined,
  })
}

/** PATCH /api/uploads/:id/collection: put a file in the meme collection (purges its AI data) or take it out */
export function useSetUploadCollection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, collection }: { id: string; collection: 'meme' | null }) =>
      apiClient.patch<{ id: string; collection: 'meme' | null; extractionStatus: string | null }>(`/api/uploads/${id}/collection`, { collection }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['storage-files'] })
      qc.invalidateQueries({ queryKey: ['uploads'] })
    },
  })
}

// ── File upload into a specific folder (with progress tracking) ─────────────

import { useCallback } from 'react'
import { useAppStore } from '../stores/app-store'
import { useT } from '../i18n'

interface XhrResult {
  status: number
  /** Parsed `error` field of a JSON error body, when present */
  error: string | null
}

/** Send XHR upload with progress tracking; resolves (never rejects) with status 0 on network failure */
function sendXhr(url: string, formData: FormData, onProgress: (pct: number) => void): Promise<XhrResult> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    })
    xhr.addEventListener('load', () => {
      let error: string | null = null
      try {
        const body: unknown = JSON.parse(xhr.responseText)
        if (typeof body === 'object' && body !== null && 'error' in body) error = String((body as { error: unknown }).error)
      } catch {
        // Non-JSON body: fall back to the status code message
      }
      resolve({ status: xhr.status, error })
    })
    xhr.addEventListener('error', () => resolve({ status: 0, error: null }))
    xhr.open('POST', url)
    xhr.withCredentials = true
    xhr.send(formData)
  })
}

/**
 * Returns an upload function that tracks progress in the global upload queue.
 * The API requires a specific description for every upload; pass it as the third argument.
 * Resolves true when the file was stored, false otherwise (the error is shown in the queue).
 */
export function useUploadToFolder() {
  const { addToUploadQueue, updateUploadProgress, updateUploadStatus, removeFromUploadQueue } = useAppStore()
  const qc = useQueryClient()
  const t = useT()

  return useCallback(async (file: File, folderId?: string | null, description?: string, collection?: 'meme'): Promise<boolean> => {
    const queueId = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    addToUploadQueue([{ id: queueId, file }])
    updateUploadStatus(queueId, 'uploading')

    const formData = new FormData()
    formData.append('file', file)
    if (folderId) formData.append('folderId', folderId)
    if (description !== undefined) formData.append('description', description)
    if (collection) formData.append('collection', collection)

    const { status, error } = await sendXhr(
      `${API_BASE}/api/uploads`,
      formData,
      (pct) => updateUploadProgress(queueId, pct),
    )

    if (status >= 200 && status < 300) {
      updateUploadStatus(queueId, 'complete')
      qc.invalidateQueries({ queryKey: collection ? ['storage-files'] : ['storage-files', folderId ?? null] })
      qc.invalidateQueries({ queryKey: ['storage-folders'] })
      qc.invalidateQueries({ queryKey: ['uploads'] })
      setTimeout(() => removeFromUploadQueue(queueId), 3000)
      return true
    }
    updateUploadStatus(
      queueId,
      'error',
      status === 0 ? t('storage.upload.networkError') : error ?? t('storage.upload.failedStatus', { status }),
    )
    return false
  }, [addToUploadQueue, updateUploadProgress, updateUploadStatus, removeFromUploadQueue, qc, t])
}

// ── Move a single file to a folder ───────────────────────────────────────────

export function useMoveUpload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      apiClient.put<{ ok: boolean }>(`/api/uploads/${id}/move`, { folderId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['storage-files'] })
      qc.invalidateQueries({ queryKey: ['uploads'] })
    },
  })
}

// ── Bulk move / delete of files and folders ──────────────────────────────────

export interface StorageBulkItems {
  fileIds: string[]
  folderIds: string[]
}

/** Refresh every storage view after items moved or disappeared */
function invalidateStorage(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['storage-files'] })
  qc.invalidateQueries({ queryKey: ['storage-folders'] })
  qc.invalidateQueries({ queryKey: ['uploads'] })
}

/** POST /api/storage/bulk/move — targetFolderId null moves to the root */
export function useBulkMove() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: StorageBulkItems & { targetFolderId: string | null }) =>
      apiClient.post<{ ok: boolean; movedFiles: number; movedFolders: number }>('/api/storage/bulk/move', body),
    onSuccess: () => invalidateStorage(qc),
  })
}

/** POST /api/storage/bulk/delete — deleted folders hand their files to the root */
export function useBulkDelete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: StorageBulkItems) => apiClient.post<{ ok: boolean }>('/api/storage/bulk/delete', body),
    onSuccess: () => invalidateStorage(qc),
  })
}
